// Dodge / Burn / Sponge tool (O) — lightens (dodge), darkens (burn), or
// adjusts saturation (sponge) of pixels in a target area.
//
// Range: "shadows" | "midtones" | "highlights" (for dodge/burn)
// Mode:  "dodge" | "burn" | "sponge"

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { screenToDoc } from "../render/Viewport";
import { mergeBBox } from "../pixel/brush";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import type { BBox } from "../pixel/brush";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export type DodgeBurnMode = "dodge" | "burn" | "sponge";
export type ToneRange = "shadows" | "midtones" | "highlights";

export interface BrushSettings {
  size: number;
  hardness: number;
  opacity: number;
  flow: number;
}

export class DodgeBurnTool implements Tool {
  readonly id = "dodge-burn";
  readonly shortcut = "O";

  mode: DodgeBurnMode = "dodge";
  range: ToneRange = "midtones";
  exposure = 50; // 0..100
  /** For sponge mode: true = saturate, false = desaturate. */
  saturate = true;
  /**
   * Photoshop's "Protect Tones" checkbox (dodge/burn only; on by default).
   * Dampens the dodge/burn effect as pixels approach the tonal extreme the
   * effect is pushing toward (white for dodge, black for burn), preventing
   * harsh highlight/shadow clipping and the color-shift that comes with it.
   * Independent of `saturate`, which only affects sponge mode (audit finding 178).
   */
  protectTones = true;
    /** Brush settings matching BrushTool interface for consistent UX. */
  settings: BrushSettings = { size: 30, hardness: 0.5, opacity: 1, flow: 1 };

  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    selMask: { mask: Uint8Array | undefined; w: number | undefined };
  } | null = null;

  /** Extract selection mask from context. */
  private getSelMask(ctx: ToolContext): { mask: Uint8Array | undefined; w: number | undefined } {
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    if (!sel || !sel.mask) return { mask: undefined, w: undefined };
    return { mask: sel.mask, w: ctx.doc!.width };
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - layer.x;
    const docY = p.y - layer.y;

    this.active = {
      layer,
      layerSnapshot: new Uint8ClampedArray(layer.pixels.data),
      lastX: docX,
      lastY: docY,
      bbox: null,
      selMask: this.getSelMask(ctx),
    };

    this.applyEffect(ctx, docX, docY);
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    const docX = p.x - this.active.layer.x;
    const docY = p.y - this.active.layer.y;

    const step = 2;
    const dx = docX - this.active.lastX;
    const dy = docY - this.active.lastY;
    const len = Math.hypot(dx, dy);
    const n = Math.max(1, Math.ceil(len / step));
    for (let i = 1; i <= n; i++) {
      const t = i / n;
      this.applyEffect(ctx, this.active.lastX + dx * t, this.active.lastY + dy * t);
    }

    this.active.lastX = docX;
    this.active.lastY = docY;
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (!this.active.bbox) { this.active = null; return; }
    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  private applyEffect(_ctx: ToolContext, docX: number, docY: number): void {
    if (!this.active) return;
    const layer = this.active.layer;
    const data = layer.pixels.data;
    const w = layer.pixels.width;
    const h = layer.pixels.height;
    const radius = this.settings.size / 2;
    const hardness = this.settings.hardness;
    const opacity = this.settings.flow;
    const r2 = radius * radius;
    const factor = this.exposure / 100;
    const { mask: selMask, w: selMaskW } = this.active.selMask;

    const minX = Math.max(0, Math.floor(docX - radius));
    const minY = Math.max(0, Math.floor(docY - radius));
    const maxX = Math.min(w - 1, Math.ceil(docX + radius));
    const maxY = Math.min(h - 1, Math.ceil(docY + radius));
    let touchX0 = w, touchY0 = h, touchX1 = -1, touchY1 = -1;

    for (let py = minY; py <= maxY; py++) {
      const dy = py + 0.5 - docY;
      const dy2 = dy * dy;
      for (let px = minX; px <= maxX; px++) {
        const dx = px + 0.5 - docX;
        if (dx * dx + dy2 >= r2) continue;
        // Soft-edge + opacity blending factor.
        const dist = Math.sqrt(dx * dx + dy2) / radius;
        const brushAlpha = dist >= 1 ? 0 : dist > hardness ? (1 - dist) / (1 - hardness || 1e-6) : 1;
        let effectStrength = brushAlpha * opacity;

        // Selection masking
        if (selMask && selMaskW) {
          const selVal = selMask[py * selMaskW + px] ?? 0;
          if (selVal === 0) continue;
          effectStrength *= selVal / 255;
        }

        // Lock-transparency: skip fully transparent pixels.
        if (layer.lockTransparency) {
          const idx = (py * w + px) * 4;
          if (data[idx + 3] === 0) continue;
        }

        const idx = (py * w + px) * 4;

        // Determine the tone range for this pixel
        const r = data[idx]!;
        const g = data[idx + 1]!;
        const b = data[idx + 2]!;
        const lum = (r * 77 + g * 150 + b * 29) >> 8;

        // Sponge: apply saturation change to all pixels regardless of tone range.
        // Tone range filter only applies to Dodge/Burn. (Photoshop behavior.)
        if (this.mode === "sponge") {
          // Sponge: adjust saturation. `saturate` controls direction:
          // true = increase saturation (sponge saturate), false = decrease (sponge desaturate).
          const gray = Math.round(r * 0.3 + g * 0.59 + b * 0.11);
          const satFactor = this.saturate ? 1 + factor * effectStrength : 1 - factor * effectStrength;
          data[idx] = clampByte(gray + (r - gray) * satFactor);
          data[idx + 1] = clampByte(gray + (g - gray) * satFactor);
          data[idx + 2] = clampByte(gray + (b - gray) * satFactor);
        } else {
          // Dodge/Burn: tone range filter applies only here, not for sponge.
          if (!inToneRange(lum, this.range)) continue;

          // Dodge/Burn: use proper color dodge/burn blend mode math.
          // Convert to linear [0,1], apply blend, convert back.
          const rN = r / 255;
          const gN = g / 255;
          const bN = b / 255;
          const dodgeBurnStrength = factor * effectStrength;

          let newR: number, newG: number, newB: number;
          if (this.mode === "dodge") {
            // Color dodge: result = src / (1 - dst), clamped
            // We use a fixed "source" of 1 (full lighten) scaled by strength
            const src = dodgeBurnStrength;
            newR = colorDodge(rN, src);
            newG = colorDodge(gN, src);
            newB = colorDodge(bN, src);
          } else {
            // Color burn: result = 1 - (1 - src) / dst, clamped
            const src = dodgeBurnStrength;
            newR = colorBurn(rN, src);
            newG = colorBurn(gN, src);
            newB = colorBurn(bN, src);
          }

          // Protect Tones: dampen how much of the dodged/burned result gets
          // blended in as the pixel's luminance nears the extreme the effect
          // is pushing toward (white for dodge, black for burn) — avoids
          // harsh highlight/shadow clipping and the color shift that comes
          // with it. Only affects dodge/burn — sponge uses `saturate`
          // instead (handled in the branch above), so the two toggles are
          // fully independent (audit finding 178).
          let mixAmount = effectStrength;
          if (this.protectTones) {
            const protectZone = 0.35; // fraction of the tonal range near the clip point that gets damped
            const headroom = this.mode === "dodge" ? (255 - lum) / 255 : lum / 255;
            const protectFactor = Math.min(1, headroom / protectZone);
            mixAmount *= protectFactor;
          }

          // Blend between original and result based on brush alpha (and,
          // when Protect Tones is on, the tone-protection factor above).
          data[idx] = clampByte((rN * (1 - mixAmount) + newR * mixAmount) * 255);
          data[idx + 1] = clampByte((gN * (1 - mixAmount) + newG * mixAmount) * 255);
          data[idx + 2] = clampByte((bN * (1 - mixAmount) + newB * mixAmount) * 255);
        }

        if (px < touchX0) touchX0 = px;
        if (py < touchY0) touchY0 = py;
        if (px > touchX1) touchX1 = px;
        if (py > touchY1) touchY1 = py;
      }
    }

    if (touchX1 >= 0) {
      const bb: BBox = { x: touchX0, y: touchY0, w: touchX1 - touchX0 + 1, h: touchY1 - touchY0 + 1 };
      this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    }
    layer.pixels.markDirty();
  }
}

function inToneRange(lum: number, range: ToneRange): boolean {
  switch (range) {
    case "shadows": return lum < 85;
    case "midtones": return lum >= 85 && lum <= 170;
    case "highlights": return lum > 170;
  }
}

/** Color dodge blend: src / (1 - dst), with clamping. */
function colorDodge(dst: number, src: number): number {
  if (dst >= 1) return 1;
  if (src <= 0) return 0;
  return Math.min(1, dst / (1 - src));
}

/** Color burn blend: 1 - (1 - src) / dst, with clamping. */
function colorBurn(dst: number, src: number): number {
  if (dst <= 0) return 0;
  if (src >= 1) return 1;
  return Math.max(0, 1 - (1 - src) / dst);
}

function clampByte(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : Math.round(v);
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}
